import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';

import {
	ClaimUsageIngestionAcceptedResponse,
	ClaimUsageIngestionBlobStatus,
	ClaimUsageIngestionErrorsResponse,
	ClaimUsageIngestionHistoryItem,
	ClaimUsageIngestionJobStatus,
	ClaimUsageIngestionStatusResponse,
	ClaimUsageIngestionUploadRequest,
} from './claim-usage-ingestion.models';
import { ClaimUsageIngestionService } from './claim-usage-ingestion.service';

const MOCK_AZUL_ORGANIZATION_ID = 'org-1';

@Injectable()
export class ClaimUsageIngestionMockService extends ClaimUsageIngestionService {
	private readonly attemptsByJobId = new Map<string, number>();
	private readonly erroredJobIds = new Set<string>();
	private readonly historyItems = this.createInitialHistoryItems();
	private readonly startedAtByJobId = new Map<string, string>();

	download(jobId: string): Observable<HttpResponse<Blob>> {
		const item = this.historyItems.find(
			(historyItem) => historyItem.preProcessing.jobId === jobId,
		);
		const fileName = item?.preProcessing.originalFileName ?? 'sinistros.txt';

		return of(
			new HttpResponse({
				body: new Blob([`mock download: ${fileName}`], { type: 'text/plain' }),
			}),
		);
	}

	getErrors(): Observable<ClaimUsageIngestionErrorsResponse> {
		return of({
			items: [
				{
					column: 'valor',
					message: 'Valor invalido para a linha.',
					rawLine: '316;2026-04;valor-invalido',
					rowNumber: 12,
				},
			],
			limit: 50,
			offset: 0,
			total: 1,
		});
	}

	getStatus(jobId: string): Observable<ClaimUsageIngestionStatusResponse> {
		const attempt = (this.attemptsByJobId.get(jobId) ?? 0) + 1;
		const startedAt =
			this.startedAtByJobId.get(jobId) ?? new Date().toISOString();
		const status = this.getMockStatus({
			attempt,
			shouldError: this.erroredJobIds.has(jobId),
		});

		this.attemptsByJobId.set(jobId, attempt);
		this.startedAtByJobId.set(jobId, startedAt);

		const statusResponse = {
			error:
				status === ClaimUsageIngestionJobStatus.FAILED
					? 'Erro mockado no processamento.'
					: undefined,
			errorLines: status === ClaimUsageIngestionJobStatus.FAILED ? 1 : 0,
			errorMessage:
				status === ClaimUsageIngestionJobStatus.FAILED
					? 'Erro mockado no processamento.'
					: undefined,
			failedRows: status === ClaimUsageIngestionJobStatus.FAILED ? 1 : 0,
			finishedAt:
				status === ClaimUsageIngestionJobStatus.COMPLETED ||
				status === ClaimUsageIngestionJobStatus.FAILED
					? new Date().toISOString()
					: undefined,
			jobId,
			lastUpdatedAt: new Date().toISOString(),
			processedLines: attempt >= 2 ? 120 : 60,
			processedRows: attempt >= 2 ? 120 : 60,
			startedAt,
			status,
			totalRows: 121,
		};

		this.updateHistoryItemFromStatus(statusResponse);

		return of(statusResponse);
	}

	list(organizationId: string): Observable<ClaimUsageIngestionHistoryItem[]> {
		return of(
			this.historyItems
				.filter(
					(item) =>
						item.preProcessing.organizationId === organizationId ||
						this.isInitialMockHistoryItem(item),
				)
				.map((item) =>
					this.cloneHistoryItemForOrganization(item, organizationId),
				),
		);
	}

	upload(
		request: ClaimUsageIngestionUploadRequest,
	): Observable<ClaimUsageIngestionAcceptedResponse> {
		const jobId = `mock-${Date.now()}`;

		this.attemptsByJobId.set(jobId, 0);
		this.startedAtByJobId.set(jobId, new Date().toISOString());
		if (request.file.name.toLowerCase().includes('erro')) {
			this.erroredJobIds.add(jobId);
		}
		this.historyItems.unshift({
			postProcessing: null,
			preProcessing: {
				blobStatus: ClaimUsageIngestionBlobStatus.STORED,
				competence: request.competence,
				estimatedProcessingWindow: 'Mock inicial',
				jobId,
				jobStatus: ClaimUsageIngestionJobStatus.QUEUED,
				operator: request.operator,
				operatorName: request.operator,
				organizationId: request.organizationId,
				organizationName: request.organizationName,
				originalFileName: request.file.name,
				responsible: request.responsible,
				size: request.file.size,
				uploadedAt: new Date().toISOString(),
				uploadedBy: request.responsible || request.uploadedBy,
			},
		});

		return of({
			jobId,
			status: ClaimUsageIngestionJobStatus.QUEUED,
			statusCode: 202,
		});
	}

	private cloneHistoryItem(
		item: ClaimUsageIngestionHistoryItem,
	): ClaimUsageIngestionHistoryItem {
		return {
			postProcessing: item.postProcessing ? { ...item.postProcessing } : null,
			preProcessing: { ...item.preProcessing },
		};
	}

	private cloneHistoryItemForOrganization(
		item: ClaimUsageIngestionHistoryItem,
		organizationId: string,
	): ClaimUsageIngestionHistoryItem {
		const clonedItem = this.cloneHistoryItem(item);

		if (this.isInitialMockHistoryItem(clonedItem)) {
			clonedItem.preProcessing.organizationId = organizationId;
		}

		return clonedItem;
	}

	private createHistoryItem({
		competence,
		error,
		errorLines,
		fileName,
		jobId,
		operatorName,
		processedLines,
		status,
		uploadedAt,
	}: {
		competence: string;
		error?: string;
		errorLines: number;
		fileName: string;
		jobId: string;
		operatorName: string;
		processedLines: number;
		status: ClaimUsageIngestionJobStatus;
		uploadedAt: string;
	}): ClaimUsageIngestionHistoryItem {
		const startedAt = new Date(
			new Date(uploadedAt).getTime() + 5 * 60 * 1000,
		).toISOString();
		const lastUpdatedAt = new Date(
			new Date(startedAt).getTime() + 10 * 60 * 1000,
		).toISOString();

		return {
			postProcessing: {
				error,
				errorLines,
				errorMessage: error,
				failedRows: errorLines,
				finalStatus: status,
				finishedAt: lastUpdatedAt,
				lastUpdatedAt,
				processedLines,
				processedRows: processedLines,
				startedAt,
				totalRows: processedLines + errorLines,
			},
			preProcessing: {
				blobStatus: ClaimUsageIngestionBlobStatus.STORED,
				competence,
				estimatedProcessingWindow: 'Ate 20 minutos',
				jobId,
				jobStatus: status,
				operatorName,
				organizationId: MOCK_AZUL_ORGANIZATION_ID,
				organizationName: 'AZUL',
				originalFileName: fileName,
				size: 1024 * 48,
				uploadedAt,
				uploadedBy: 'Igor Barreto',
			},
		};
	}

	private createInitialHistoryItems(): ClaimUsageIngestionHistoryItem[] {
		return [
			this.createHistoryItem({
				competence: '2026-05',
				errorLines: 0,
				fileName: 'sinistros_azul_2026_05.txt',
				jobId: 'mock-history-success-2026-05',
				operatorName: 'CNU',
				processedLines: 12450,
				status: ClaimUsageIngestionJobStatus.COMPLETED,
				uploadedAt: '2026-05-08T13:20:00.000Z',
			}),
			this.createHistoryItem({
				competence: '2026-04',
				error: 'Arquivo contém linhas com layout inválido',
				errorLines: 37,
				fileName: 'sinistros_azul_2026_04_erro.txt',
				jobId: 'mock-history-error-2026-04',
				operatorName: 'CNU',
				processedLines: 11980,
				status: ClaimUsageIngestionJobStatus.FAILED,
				uploadedAt: '2026-04-08T13:20:00.000Z',
			}),
			this.createHistoryItem({
				competence: '2026-03',
				error: 'Arquivo processado com linhas rejeitadas',
				errorLines: 5,
				fileName: 'sinistros_azul_2026_03_parcial.txt',
				jobId: 'mock-history-partial-2026-03',
				operatorName: 'CNU',
				processedLines: 12000,
				status: ClaimUsageIngestionJobStatus.PARTIALLY_COMPLETED,
				uploadedAt: '2026-03-08T13:20:00.000Z',
			}),
			this.createHistoryItem({
				competence: '2026-06',
				errorLines: 2,
				fileName: 'sinistros_azul_2026_06.txt',
				jobId: 'mock-history-processing-2026-06',
				operatorName: 'SULAMERICA',
				processedLines: 4500,
				status: ClaimUsageIngestionJobStatus.PROCESSING,
				uploadedAt: '2026-06-08T13:20:00.000Z',
			}),
			this.createHistoryItem({
				competence: '2026-07',
				errorLines: 0,
				fileName: 'sinistros_azul_2026_07.txt',
				jobId: 'mock-history-waiting-2026-07',
				operatorName: 'CNU',
				processedLines: 0,
				status: ClaimUsageIngestionJobStatus.QUEUED,
				uploadedAt: '2026-07-08T13:20:00.000Z',
			}),
		];
	}

	private getMockStatus({
		attempt,
		shouldError,
	}: {
		attempt: number;
		shouldError: boolean;
	}): ClaimUsageIngestionJobStatus {
		if (attempt < 2) {
			return ClaimUsageIngestionJobStatus.PROCESSING;
		}

		return shouldError
			? ClaimUsageIngestionJobStatus.FAILED
			: ClaimUsageIngestionJobStatus.COMPLETED;
	}

	private isInitialMockHistoryItem(
		item: ClaimUsageIngestionHistoryItem,
	): boolean {
		return item.preProcessing.jobId.startsWith('mock-history-');
	}

	private updateHistoryItemFromStatus(
		statusResponse: ClaimUsageIngestionStatusResponse,
	): void {
		const item = this.historyItems.find(
			(historyItem) => historyItem.preProcessing.jobId === statusResponse.jobId,
		);

		if (!item) {
			return;
		}

		item.preProcessing = {
			...item.preProcessing,
			jobStatus: statusResponse.status,
		};
		item.postProcessing = {
			error: statusResponse.errorMessage,
			errorLines: statusResponse.failedRows,
			errorMessage: statusResponse.errorMessage,
			failedRows: statusResponse.failedRows,
			finalStatus: statusResponse.status,
			finishedAt: statusResponse.finishedAt,
			lastUpdatedAt: statusResponse.lastUpdatedAt,
			processedLines: statusResponse.processedRows,
			processedRows: statusResponse.processedRows,
			startedAt: statusResponse.startedAt,
			totalRows: statusResponse.totalRows,
		};
	}
}
